/* Sending a clip to somebody with no account here.
 *
 * A sheet rather than a menu item, because this is the one action on a clip
 * whose audience is not the crew, and the person pressing it should see what
 * leaves the building before it does: the note, the sound, the level, when
 * it was recorded -- and not the farm or who kept it. That list is the confirmation.
 *
 * The token exists in the browser only between "Lag lenke" and closing the
 * sheet. The receiver keeps its hash, so a link made earlier cannot be shown
 * again -- only counted, opened-or-not, and stopped. That is said plainly
 * rather than worked around.
 *
 * Overlay manners as Telling.tsx: focus trapped and restored, Escape closes,
 * the page behind does not scroll. Portalled to <body> because the clip cards
 * sit in a sideways-scrolling shelf that would otherwise clip it.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { klipp, type ClipShare, type ClipShareMade, type SiteClip } from '../api'
import { useLang, useT } from '../lang'

function span(s: number): string {
  const v = Math.max(1, Math.round(s))
  return v >= 90 ? `${Math.floor(v / 60)}:${String(v % 60).padStart(2, '0')}` : `${v} s`
}

export function DelSheet({ clip, onClose }: { clip: SiteClip; onClose: () => void }) {
  const t = useT()
  const { lang } = useLang()
  const locale = lang === 'nb' ? 'nb-NO' : 'en-GB'
  const [out, setOut] = useState<ClipShare[] | null>(null)
  const [made, setMade] = useState<ClipShareMade | null>(null)
  const [copied, setCopied] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const wrap = useRef<HTMLDivElement>(null)
  const first = useRef<HTMLButtonElement>(null)

  const refresh = () => klipp.shares(clip.clip_id)
    .then((r) => setOut(r.items))
    .catch((e) => setError(e instanceof Error ? e.message : String(e)))

  useEffect(() => { void refresh() }, [clip.clip_id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const back = document.activeElement as HTMLElement | null
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    first.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); return }
      if (e.key !== 'Tab' || !wrap.current) return
      const live = wrap.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"])')
      if (!live.length) return
      const a = live[0]!, z = live[live.length - 1]!
      if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus() }
      else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus() }
    }
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.body.style.overflow = prev
      document.removeEventListener('keydown', onKey, true)
      back?.focus?.()
    }
  }, [onClose])

  const url = made ? `${window.location.origin}${made.path}` : ''

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2500)
    } catch {
      setError(t('Could not copy. Select the link and copy it.'))
    }
  }

  const make = async () => {
    setBusy(true); setError(null)
    try {
      const m = await klipp.share(clip.clip_id)
      setMade(m)
      void refresh()
      // Straight to the clipboard when the browser allows it; the field is
      // there for when it does not.
      try {
        await navigator.clipboard.writeText(`${window.location.origin}${m.path}`)
        setCopied(true)
        window.setTimeout(() => setCopied(false), 2500)
      } catch { /* the field and the button remain */ }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const stop = async (ids: string[]) => {
    setBusy(true); setError(null)
    try {
      for (const id of ids) await klipp.unshare(clip.clip_id, id)
      if (made && ids.includes(made.share_id)) setMade(null)
      await refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const mine = made ? out?.find((x) => x.share_id === made.share_id) : undefined
  const others = (out ?? []).filter((x) => x.share_id !== made?.share_id)
  const othersOpened = others.reduce((a, x) => a + x.opened_count, 0)
  const opened = (n: number) => n === 0 ? t('Not yet') : `${n} ${n === 1 ? t('time') : t('times')}`
  const date = (iso: string) => new Date(iso).toLocaleDateString(locale,
    { day: 'numeric', month: 'short', year: 'numeric' })

  return createPortal(
    <div className="ds-back" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div ref={wrap} className="ds" role="dialog" aria-modal="true" aria-labelledby="ds-title">
        <div className="ds-top">
          <span className="ds-grab" aria-hidden="true" />
          <button ref={first} type="button" className="ds-x" onClick={onClose} aria-label={t('Close')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>
        <h2 id="ds-title" className="ds-title">{t('Share with anyone')}</h2>

        <div className="ds-clip">
          <span className="ds-clip-play" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.2-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5z" /></svg>
          </span>
          <span className="ds-clip-note">{clip.note || t('No note')}</span>
          <span className="ds-clip-len">{span(clip.duration_s)}</span>
        </div>

        <div className="ds-sees">
          <span className="ds-cap">{t('Whoever opens the link sees')}</span>
          <span><Tick />{t('The note, the sound and the timeline')}</span>
          <span><Tick />{t('The sound level in dB')}</span>
          <span><Tick />{t('When it was recorded')}</span>
          <span className="ds-not"><Cross />{t('Not the farm or who kept it')}</span>
        </div>

        {made ? (
          <>
            <div className="ds-link">
              <label htmlFor="ds-url" className="ds-cap">{t('The link')}</label>
              <div>
                <input id="ds-url" readOnly value={url} onFocus={(e) => e.currentTarget.select()} />
                <button type="button" className={`ds-copy${copied ? ' done' : ''}`} onClick={() => void copy()}>
                  {copied ? t('Copied') : t('Copy')}
                </button>
              </div>
            </div>
            <div className="ds-facts">
              <div><span>{t('Works until')}</span><strong>{date(made.expires_at)}</strong></div>
              <div><span>{t('Opened')}</span><strong>{opened(mine?.opened_count ?? 0)}</strong></div>
            </div>
            <button type="button" className="ds-stop" disabled={busy}
              onClick={() => void stop([made.share_id])}>{t('Stop the link')}</button>
          </>
        ) : (
          <>
            <button type="button" className="ds-make" disabled={busy} onClick={() => void make()}>
              {busy ? t('Creating') : t('Create link')}
            </button>
            <p className="ds-small">{t('The link works for 90 days. You can stop it at any time.')}</p>
          </>
        )}

        {others.length > 0 && (
          <div className="ds-others">
            <span>
              {others.length} {others.length === 1 ? t('earlier link is out') : t('earlier links are out')}
              {' · '}{t('opened')} {opened(othersOpened).toLowerCase()}
            </span>
            <button type="button" className="ds-stop" disabled={busy}
              onClick={() => void stop(others.map((x) => x.share_id))}>
              {others.length === 1 ? t('Stop it') : t('Stop them')}
            </button>
          </div>
        )}
        {error && <p className="ds-err" role="alert">{error}</p>}
      </div>
    </div>,
    document.body,
  )
}

function Tick() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1f6e45" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>
}
function Cross() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
}
